import {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  useRef,
  CSSProperties,
} from "react"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { coldarkDark as codeStyle } from "react-syntax-highlighter/dist/esm/styles/prism"

import { Textarea } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"
import { diffTextHtml } from "@/lib/diffTextHtml"

type TextareaProps = React.ComponentProps<typeof Textarea>

export interface AITrackedTextareaProps extends Omit<TextareaProps, "value"> {
  className?: string
  textareaClassName?: string
  defaultValue: string
  /**
   * Original/baseline value to compare against (typically the last saved version)
   */
  originalValue: string
  /**
   * When true, shows diff highlighting overlay
   */
  showTracking: boolean
  /**
   * When provided and showTracking is false, renders a syntax-highlighted overlay for the given language.
   */
  highlightLanguage?: string
  /**
   * Allows turning off the resize handle on the outer wrapper (e.g., modal).
   */
  enableResize?: boolean
  /**
   * Optional classes applied to the diff overlay when showTracking is true.
   */
  trackingOverlayClassName?: string
}

/**
 * Textarea with inline change tracking.
 * When tracking is enabled, additions are shown in green and deletions in red with strikethrough.
 */
const AITrackedTextarea = forwardRef<
  HTMLTextAreaElement,
  AITrackedTextareaProps
>(
  (
    {
      className,
      textareaClassName,
      defaultValue,
      originalValue,
      showTracking,
      onChange,
      style,
      highlightLanguage,
      enableResize = true,
      trackingOverlayClassName,
      ...restProps
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>(defaultValue)
    const highlightOverlayRef = useRef<HTMLDivElement>(null)
    const codeFontFamily =
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
    const codeTypography = {
      fontFamily: codeFontFamily,
      fontSize: "14px",
      lineHeight: "1.5",
      letterSpacing: "0",
    } as const

    useEffect(() => {
      setInputValue(defaultValue)
    }, [defaultValue])

    // Generate diff HTML when tracking is enabled
    const diffHtml = useMemo(() => {
      if (!showTracking) return ""
      return diffTextHtml(originalValue ?? "", inputValue ?? "")
    }, [showTracking, originalValue, inputValue])

    const trackingHtml = showTracking ? diffHtml : ""

    const isHighlighting = !!highlightLanguage && !showTracking

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value)
      onChange?.(e)
    }

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
      if (highlightOverlayRef.current) {
        highlightOverlayRef.current.scrollTop = e.currentTarget.scrollTop
        highlightOverlayRef.current.scrollLeft = e.currentTarget.scrollLeft
      }
      restProps.onScroll?.(e)
    }

    const baseStyle: CSSProperties = showTracking
      ? {
          color: "transparent",
          caretColor: "transparent",
          pointerEvents: "none",
          height: "100%",
          resize: "none",
        }
      : isHighlighting
        ? {
            color: "transparent",
            caretColor: "rgb(96, 165, 250)",
            backgroundColor: "transparent",
            height: "100%",
            resize: "none",
            ...codeTypography,
          }
        : { height: "100%" }

    const textareaStyle: CSSProperties = {
      ...baseStyle,
      ...style,
    }

    return (
      <div
        className={cn(
          "relative min-h-[80px]",
          enableResize ? "resize-y overflow-hidden" : "overflow-hidden",
          className
        )}
      >
        {showTracking && !!trackingHtml && (
          <pre
            aria-hidden="true"
            className={cn(
              "absolute left-0 top-0 right-0 bottom-0 z-10 overflow-auto whitespace-pre-wrap break-words font-sans text-sm px-3 py-2 border-2 border-transparent bg-transparent rounded-md",
              "[&_ins.diff-add]:bg-green-100 [&_ins.diff-add]:text-green-800 [&_ins.diff-add]:no-underline",
              "[&_del.diff-del]:bg-red-100 [&_del.diff-del]:text-red-800 [&_del.diff-del]:line-through",
              "dark:[&_ins.diff-add]:bg-green-900/40 dark:[&_ins.diff-add]:text-green-300",
              "dark:[&_del.diff-del]:bg-red-900/40 dark:[&_del.diff-del]:text-red-300",
              trackingOverlayClassName
            )}
            dangerouslySetInnerHTML={{ __html: trackingHtml }}
          />
        )}

        {!showTracking && highlightLanguage && (
          <div
            ref={highlightOverlayRef}
            className="absolute left-0 top-0 right-0 bottom-0 z-0 overflow-auto rounded-md border border-slate-200 dark:border-slate-700 bg-slate-950/80 pointer-events-none"
          >
            <SyntaxHighlighter
              language={highlightLanguage}
              style={codeStyle}
              customStyle={{
                margin: 0,
                padding: "8px 12px",
                backgroundColor: "transparent",
                minHeight: "100%",
                boxSizing: "border-box",
                ...codeTypography,
              }}
              wrapLines
              lineProps={{ style: { whiteSpace: "pre-wrap" } }}
            >
              {inputValue || " "}
            </SyntaxHighlighter>
          </div>
        )}

        <Textarea
          {...restProps}
          ref={ref}
          value={inputValue}
          onChange={handleChange}
          onScroll={handleScroll}
          readOnly={showTracking}
          aria-label={
            showTracking
              ? "Text with change tracking enabled (read-only). Additions and deletions are highlighted."
              : restProps["aria-label"]
          }
          style={textareaStyle}
          className={cn(
            "relative pr-10 resize-none",
            showTracking ? "z-0 bg-transparent border-transparent" : "z-10",
            isHighlighting &&
              "bg-transparent border border-slate-200 dark:border-slate-700 text-transparent caret-white",
            textareaClassName
          )}
        />
      </div>
    )
  }
)

AITrackedTextarea.displayName = "AITrackedTextarea"

export default AITrackedTextarea
